import type { ConnectionMetadata, IntegrationRuntimeStatus, IntegrationRuntimeSummary } from '../types/integration'
import type { StorageConnectionMetadata, WorkspaceFileMetadata } from '../types/schema'
import type { AiRetrievalRecord } from './ai/retrieval/retrievalTypes'

export const RUNTIME_STATUS_LABELS: Record<IntegrationRuntimeStatus, string> = {
  DISCONNECTED: 'Disconnected',
  CONNECTING: 'Connecting',
  ACTIVE: 'Active',
  DEGRADED: 'Degraded',
  RATE_LIMITED: 'Rate limited',
  REAUTH_REQUIRED: 'Re-auth required',
  FAILED: 'Failed',
  DISABLED: 'Disabled',
}

export const runtimeStatusTone = (status?: IntegrationRuntimeStatus): 'success' | 'warning' | 'critical' | 'muted' | 'accent' => {
  if (status === 'ACTIVE') return 'success'
  if (status === 'CONNECTING' || status === 'DEGRADED') return 'warning'
  if (status === 'FAILED' || status === 'RATE_LIMITED' || status === 'REAUTH_REQUIRED') return 'critical'
  if (status === 'DISABLED') return 'muted'
  return 'muted'
}

export const runtimeStatusLabel = (summary?: IntegrationRuntimeSummary | null, fallback?: string) =>
  summary?.lifecycleStatus ? RUNTIME_STATUS_LABELS[summary.lifecycleStatus] : fallback ?? 'Unknown'

export const runtimeSummaryText = (summary?: IntegrationRuntimeSummary | null, fallback?: string) =>
  summary?.summary?.trim() || fallback || 'No runtime summary has been reported yet.'

const timestampIso = (value: unknown): string | null => {
  if (!value) return null
  if (typeof value === 'string') return Number.isFinite(Date.parse(value)) ? new Date(value).toISOString() : null
  if (typeof value === 'object' && 'toDate' in value && typeof value.toDate === 'function') {
    const date = value.toDate()
    return date instanceof Date ? date.toISOString() : null
  }
  return null
}

const uploadSummary = (files: WorkspaceFileMetadata[]) => {
  const visible = files.filter((file) => file.status === 'ACTIVE')
  const driveReferences = visible.filter((file) => file.source === 'GOOGLE_DRIVE')
  const aiUploads = visible.filter((file) => file.artifactType === 'AI_MAGIC_UPLOAD')
  const unavailable = files.filter((file) => file.status === 'UNAVAILABLE')
  const totalBytes = visible.reduce((sum, file) => sum + Number(file.sizeBytes ?? 0), 0)
  return { visible, driveReferences, aiUploads, unavailable, totalBytes }
}

export const buildIntegrationRuntimeRecords = (input: {
  workspaceId: string
  connections: ConnectionMetadata[]
  legacyConnections?: StorageConnectionMetadata[]
  files?: WorkspaceFileMetadata[]
  nowIso?: string
}): AiRetrievalRecord[] => {
  const nowIso = input.nowIso ?? new Date().toISOString()
  const records: AiRetrievalRecord[] = []
  input.connections.forEach((connection) => {
    const summary = connection.runtime
    if (!summary) return
    records.push({
      id: `integration:${connection.id}`,
      workspaceId: input.workspaceId,
      scope: 'governance_activity',
      layer: summary.lifecycleStatus === 'ACTIVE' ? 'active_operational' : 'execution_risk',
      type: 'UNKNOWN',
      title: `${connection.providerId} integration ${runtimeStatusLabel(summary).toLowerCase()}`,
      summary: [
        runtimeSummaryText(summary),
        summary.healthSignals?.length ? `Signals: ${summary.healthSignals.join(', ')}` : '',
        summary.sync?.state ? `Sync: ${summary.sync.state}` : '',
        summary.errors?.code ? `Error: ${summary.errors.code}` : '',
      ].filter(Boolean).join(' | '),
      updatedAtIso: timestampIso(summary.updatedAt) ?? timestampIso(connection.runtimeUpdatedAt) ?? nowIso,
      riskSignals: [
        summary.lifecycleStatus !== 'ACTIVE' ? summary.lifecycleStatus : '',
        ...(summary.healthSignals ?? []),
      ].filter(Boolean),
      urgency: ['FAILED', 'RATE_LIMITED', 'REAUTH_REQUIRED'].includes(summary.lifecycleStatus) ? 20 : summary.lifecycleStatus === 'DEGRADED' ? 12 : 4,
      isBlocked: ['FAILED', 'RATE_LIMITED', 'REAUTH_REQUIRED'].includes(summary.lifecycleStatus),
      source: 'FIRESTORE_CACHE',
      metadata: { providerId: connection.providerId, connectionId: connection.id, aiUsable: summary.aiUsable },
    })
  })
  ;(input.legacyConnections ?? []).forEach((connection) => {
    const summary = connection.runtime
    if (!summary) return
    records.push({
      id: `legacy-integration:${connection.id}`,
      workspaceId: input.workspaceId,
      scope: 'governance_activity',
      layer: summary.lifecycleStatus === 'ACTIVE' ? 'active_operational' : 'execution_risk',
      type: 'UNKNOWN',
      title: `${connection.displayName} legacy runtime`,
      summary: runtimeSummaryText(summary),
      updatedAtIso: timestampIso(summary.updatedAt) ?? timestampIso(connection.runtimeUpdatedAt) ?? nowIso,
      riskSignals: summary.healthSignals ?? [],
      urgency: summary.lifecycleStatus === 'ACTIVE' ? 3 : 10,
      isBlocked: ['FAILED', 'RATE_LIMITED', 'REAUTH_REQUIRED'].includes(summary.lifecycleStatus),
      source: 'FIRESTORE_CACHE',
      metadata: { provider: connection.provider, connectionId: connection.id, aiUsable: summary.aiUsable },
    })
  })
  if (input.files?.length) {
    const files = uploadSummary(input.files)
    records.push({
      id: `uploads:${input.workspaceId}`,
      workspaceId: input.workspaceId,
      scope: 'uploads',
      layer: files.unavailable.length ? 'execution_risk' : 'active_operational',
      type: 'UPLOAD',
      title: 'Workspace upload context',
      summary: `${files.visible.length} active files, ${files.driveReferences.length} Drive references, ${files.aiUploads.length} AI Magic uploads, ${files.unavailable.length} unavailable references.`,
      updatedAtIso: nowIso,
      riskSignals: files.unavailable.length ? ['unavailable provider references'] : [],
      urgency: files.unavailable.length ? 10 : 3,
      isBlocked: files.unavailable.length > 0,
      source: 'FIRESTORE_CACHE',
      metadata: {
        activeFileCount: files.visible.length,
        driveReferenceCount: files.driveReferences.length,
        aiUploadCount: files.aiUploads.length,
        unavailableCount: files.unavailable.length,
        totalBytes: files.totalBytes,
      },
    })
  }
  return records.slice(0, 24)
}
